import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

const Profile = () => {
    const { user, updateUser } = useAuth();
    const { config } = useConfig();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: user?.name || '',
        last_name: user?.last_name || ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const primaryColor = config?.primaryColor || '#1e40af';

    const handleSave = async () => {
        if (!formData.name || !formData.last_name) {
            setMessage({ type: 'error', text: 'Por favor completa todos los campos' });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await api.put('/users/update-profile', formData);
            updateUser(formData);
            setMessage({ type: 'success', text: response.data.message });
            setIsEditing(false);
        } catch (error) {
            console.error(error);
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Error al actualizar el perfil' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="animate-fadeIn pb-32">
            <header className="mb-10 flex flex-col items-center text-center pt-8">
                <div
                    className="w-24 h-24 rounded-[40px] flex items-center justify-center text-white text-4xl font-black mb-6 shadow-2xl relative"
                    style={{ backgroundColor: primaryColor }}
                >
                    {user?.name?.[0].toUpperCase()}
                    <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-lg text-lg">✨</div>
                </div>
                <h1 className="text-3xl font-black text-white tracking-tight">{user?.name} {user?.last_name}</h1>
                <p className="text-white/40 font-bold uppercase tracking-widest text-[10px] mt-2">{user?.role} en NeuroUpdate</p>
                
                {!isEditing && (
                    <button 
                        onClick={() => setIsEditing(true)}
                        className="mt-6 px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-[10px] font-black uppercase tracking-widest border border-white/20 transition-all"
                    >
                        Editar Perfil
                    </button>
                )}
            </header>

            <div className="bg-white/10 backdrop-blur-2xl rounded-[40px] p-10 border border-white/20 shadow-2xl space-y-10 relative overflow-hidden max-w-2xl mx-auto">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500 rounded-full blur-[100px] opacity-20"></div>
                
                {message.text && (
                    <div className={`p-4 rounded-2xl text-center text-xs font-bold uppercase tracking-widest ${message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                        {message.text}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
                    {isEditing ? (
                        <>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Nombre</label>
                                <input 
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 focus:border-white/30 outline-none transition-all"
                                    placeholder="Nombre"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Apellido</label>
                                <input 
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                    className="w-full text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 focus:border-white/30 outline-none transition-all"
                                    placeholder="Apellido"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Correo Electrónico</label>
                                <p className="text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 shadow-inner overflow-hidden text-ellipsis">{user?.email}</p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Identificación</label>
                                <p className="text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 shadow-inner">{user?.identification || 'N/A'}</p>
                            </div>
                        </>
                    )}
                </div>

                {isEditing && (
                    <div className="flex gap-4 relative z-10">
                        <button 
                            onClick={handleSave}
                            disabled={loading}
                            className="flex-grow py-4 bg-white text-[#1c0042] rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-white/90 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                        <button 
                            onClick={() => {
                                setIsEditing(false);
                                setFormData({ name: user?.name, last_name: user?.last_name });
                                setMessage({ type: '', text: '' });
                            }}
                            className="px-8 py-4 bg-white/5 text-white rounded-2xl font-black text-xs uppercase tracking-widest border border-white/10 hover:bg-white/10 transition-all"
                        >
                            Cancelar
                        </button>
                    </div>
                )}

                <div className="pt-10 border-t border-white/10 flex flex-col items-center gap-6 relative z-10">
                    <p className="text-xs text-white/60 font-black uppercase tracking-widest text-center leading-relaxed">Pase de Acceso <br />NeuroUpdate Virtual ID</p>
                    <div className="w-64 bg-white p-6 rounded-[35px] shadow-[0_0_50px_rgba(157,78,221,0.2)] flex flex-col items-center gap-4 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                        <div className="relative z-10 p-2 bg-white rounded-2xl">
                            {user?.id && (
                                <QRCodeSVG 
                                    value={user.id.toString()} 
                                    size={180}
                                    fgColor="#1c0042"
                                    level="H"
                                />
                            )}
                        </div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">Muestra este código para ingresar</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
